import { useCallback, useEffect, useRef, useState } from 'react';
import { createCancelSource, fetchDetail, fetchTimeline, isCancel } from '../api';
import { openCreativeChat } from '../../../../services/creativeChatSocket';

const sortLogs = (logs) =>
  [...logs].sort((a, b) => {
    const t = Number(a?.created_time || 0) - Number(b?.created_time || 0);
    if (t !== 0) return t;
    return Number(a?.id || 0) - Number(b?.id || 0);
  });

const mergeLogs = (existing, incoming) => {
  const list = Array.isArray(incoming) ? incoming : [incoming];
  const clean = list.filter(Boolean);
  if (!clean.length) return existing;

  const seen = new Set(existing.map((l) => String(l?.id)));
  const added = clean.filter((l) => {
    const key = String(l?.id);
    if (l?.id == null || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  if (!added.length) return existing;
  return sortLogs([...existing, ...added]);
};

/**
 * Detail + timeline for one creative.
 *
 * There is no `/versions` endpoint on this panel — the content writer's
 * "drafts" are just the current `variations` array on `detail`, which
 * `applyDetailPatch` keeps in sync after a submit. Do not add a versions
 * fetch here; there is nothing server-side to fetch.
 *
 * `applyIncoming(logOrLogs)` is the single write seam for new activity. Today
 * it is fed by the responses of pick / message / upload / submit-variations;
 * a socket subscription would plug into exactly the same function later.
 * There is no polling here.
 */
const useCreativeThread = (bscId, { enabled = true, seedRow = null } = {}) => {
  const [detail, setDetail] = useState(seedRow);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState(null);

  const mountedRef = useRef(true);
  const detailReqRef = useRef(0);
  const logsReqRef = useRef(0);
  const sourcesRef = useRef([]);
  const seedRowRef = useRef(seedRow);
  seedRowRef.current = seedRow;

  const cancelAll = useCallback((reason) => {
    sourcesRef.current.forEach((s) => {
      try {
        s.cancel(reason);
      } catch (e) {
        /* already settled */
      }
    });
    sourcesRef.current = [];
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      cancelAll('component unmounted');
    };
  }, [cancelAll]);

  const track = useCallback(() => {
    const source = createCancelSource();
    sourcesRef.current.push(source);
    return source;
  }, []);

  const loadDetail = useCallback(async () => {
    if (!bscId) return;
    const reqId = detailReqRef.current + 1;
    detailReqRef.current = reqId;
    const source = track();
    setDetailLoading(true);
    setDetailError(null);
    try {
      const data = await fetchDetail(bscId, source.token);
      if (!mountedRef.current || reqId !== detailReqRef.current) return;
      if (data) setDetail(data);
    } catch (err) {
      if (isCancel(err)) return;
      if (!mountedRef.current || reqId !== detailReqRef.current) return;
      setDetailError(err?.message || 'Unable to load this creative.');
    } finally {
      if (mountedRef.current && reqId === detailReqRef.current) setDetailLoading(false);
    }
  }, [bscId, track]);

  const loadLogs = useCallback(async () => {
    if (!bscId) return;
    const reqId = logsReqRef.current + 1;
    logsReqRef.current = reqId;
    const source = track();
    setLogsLoading(true);
    setLogsError(null);
    try {
      const data = await fetchTimeline(bscId, source.token);
      if (!mountedRef.current || reqId !== logsReqRef.current) return;
      setLogs(sortLogs(Array.isArray(data?.data) ? data.data : []));
    } catch (err) {
      if (isCancel(err)) return;
      if (!mountedRef.current || reqId !== logsReqRef.current) return;
      setLogsError(err?.message || 'Unable to load the activity.');
    } finally {
      if (mountedRef.current && reqId === logsReqRef.current) setLogsLoading(false);
    }
  }, [bscId, track]);

  useEffect(() => {
    if (!enabled || !bscId) return undefined;
    setDetail(seedRowRef.current || null);
    setLogs([]);
    loadDetail();
    loadLogs();
    return () => cancelAll('creative changed');
  }, [enabled, bscId, loadDetail, loadLogs, cancelAll]);

  /** The seam: fold new log rows (from an action response, later a socket). */
  const applyIncoming = useCallback((logOrLogs) => {
    if (!mountedRef.current) return;
    setLogs((prev) => mergeLogs(prev, logOrLogs));
  }, []);

  // Live relay join. Content writers can message at any status — `canMessage()`
  // in this panel's config.js is unconditionally `true` — so there is no
  // "locked" gate here, unlike the designer panel: join whenever the thread
  // is open.
  useEffect(() => {
    if (!enabled || !bscId) return undefined;
    return openCreativeChat(bscId, { onMessage: applyIncoming });
  }, [enabled, bscId, applyIncoming]);

  /** Fold an action's status/variations change into the locally held detail row. */
  const applyDetailPatch = useCallback((patch) => {
    if (!mountedRef.current || !patch) return;
    setDetail((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  return {
    detail,
    detailLoading,
    detailError,
    reloadDetail: loadDetail,

    logs,
    logsLoading,
    logsError,
    reloadLogs: loadLogs,

    applyIncoming,
    applyDetailPatch,
  };
};

export default useCreativeThread;
